import { notificationsByPatientId, notificationsByPatientIdFacilityIdAndCreatedAt } from '@/graphql/queries';
import { updateNotification } from '@/graphql/mutations';
import { onCreateNotification, onUpdateNotification } from '@/graphql/subscriptions';
import {
  ModelSortDirection,
  NotificationsByPatientIdFacilityIdAndCreatedAtQuery,
  NotificationsByPatientIdQuery,
  OnCreateNotificationSubscription,
  UpdateNotificationMutation,
  OnUpdateNotificationSubscription
} from '@/API';
import { notMatchCustomErrorMessage } from '@/lib/sentryClient';
import * as Sentry from '@sentry/react';
import { executeQuery, executeSubscribe } from '@/client/graphql';

// 患者ごとの未読数を取得
export const fetchUnreadCountByPatientId = async (patientId: string) => {
  try {
    const res = await executeQuery<NotificationsByPatientIdQuery>(notificationsByPatientId, {
      patientId,
      filter: { isRead: { eq: false } },
      limit: 1000
    });

    const unreadCount = res?.notificationsByPatientId?.items.length ?? 0;
    return unreadCount;
  } catch (error) {
    if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
      Sentry.captureException(error);
    }
    console.error('Error fetching unread count by patientId:', error);
    return 0;
  }
};

// 各施設の未読数を取得
export const fetchUnreadCountByPatientIdAndFacilityId = async (patientId: string) => {
  try {
    const res = await executeQuery<NotificationsByPatientIdQuery>(notificationsByPatientId, {
      patientId,
      filter: { isRead: { eq: false } },
      limit: 1000
    });

    const unreadCounts = res?.notificationsByPatientId?.items.reduce(
      (acc: Record<string, number>, notification: any) => {
        const { facilityId } = notification;
        acc[facilityId] = (acc[facilityId] || 0) + 1;
        return acc;
      },
      {}
    );
    return unreadCounts;
  } catch (error) {
    if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
      Sentry.captureException(error);
    }
    console.error('Error fetching unread count by patientId and facilityId:', error);
    return {};
  }
};

// 通知の取得
export const fetchNotifications = async (patientIdFacilityId: string, nextToken: string | null) => {
  try {
    const res = await executeQuery<NotificationsByPatientIdFacilityIdAndCreatedAtQuery>(
      notificationsByPatientIdFacilityIdAndCreatedAt,
      {
        patientIdFacilityId,
        sortDirection: ModelSortDirection.DESC,
        limit: 5,
        nextToken
      }
    );
    if (!res) {
      return null;
    }

    return res.notificationsByPatientIdFacilityIdAndCreatedAt;
  } catch (error) {
    if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
      Sentry.captureException(error);
    }
    console.error('Error fetching notifications:', error);
    return null;
  }
};

// 通知を既読にする
export const updateNotificationStatus = async (id: string) => {
  try {
    const res = await executeQuery<UpdateNotificationMutation>(updateNotification, {
      input: {
        id,
        isRead: true
      }
    });

    if (!res) {
      return null;
    }
    return res;
  } catch (error) {
    if (error instanceof Error && notMatchCustomErrorMessage(error.name)) {
      Sentry.captureException(error);
    }
    console.error('Error updating notification status:', error);
    return null;
  }
};

// 通知の作成サブスクリプション
export const subscribeToNewNotifications = (
  patientIdFacilityId: string,
  next: (value: OnCreateNotificationSubscription) => void,
  errorFunc?: (error: any) => void
) => {
  return executeSubscribe<OnCreateNotificationSubscription>(
    onCreateNotification,
    next,
    {
      filter: {
        patientIdFacilityId: { eq: patientIdFacilityId }
      }
    },
    errorFunc
  );
};

// 通知の更新サブスクリプション
export const subscribeToUpdatedNotifications = (
  patientIdFacilityId: string,
  next: (value: OnUpdateNotificationSubscription) => void,
  errorFunc?: (error: any) => void
) => {
  return executeSubscribe<OnUpdateNotificationSubscription>(
    onUpdateNotification,
    next,
    {
      filter: {
        patientIdFacilityId: { eq: patientIdFacilityId }
      }
    },
    errorFunc
  );
};

// 通知の作成サブスクリプション（patientIdベース）
export const subscribeToNewNotificationsByPatientId = (
  patientId: string,
  next: (value: OnCreateNotificationSubscription) => void,
  errorFunc?: (error: any) => void
) => {
  return executeSubscribe<OnCreateNotificationSubscription>(
    onCreateNotification,
    next,
    {
      filter: {
        patientId: { eq: patientId }
      }
    },
    errorFunc
  );
};

// 通知の更新サブスクリプション（patientIdベース）
export const subscribeToUpdatedNotificationsByPatientId = (
  patientId: string,
  next: (value: OnUpdateNotificationSubscription) => void,
  errorFunc?: (error: any) => void
) => {
  return executeSubscribe<OnUpdateNotificationSubscription>(
    onUpdateNotification,
    next,
    {
      filter: {
        patientId: { eq: patientId }
      }
    },
    errorFunc
  );
};
